import { useEffect, useState } from "react";
import "./index.css";
import { motion } from "motion/react";
import { ChatInput } from "./components/ChatInput";
import { MessageList } from "./components/MessageList";
import { useChat } from "./hooks/useChat";

export function App() {
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    clearError,
    messagesEndRef,
    showCheckpoint,
    isCheckpointSubmitting,
    handleCheckpointSubmit,
    isBlocked,
    weeklyWarningMessage,
    setWeeklyWarningMessage,
  } = useChat();

  // Dynamic Expiration Timer Logic
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);

  const [isIframe, setIsIframe] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);

  useEffect(() => {
    // Check if we are in an iframe (for the web platform)
    let inIframe = false;
    try {
      inIframe = window.self !== window.top;
    } catch (e) {
      inIframe = true;
    }

    // Check the URL for a mobile app flag
    // This looks for ?hideHeader=true(android/iOS) in the address bar
    const urlParams = new URLSearchParams(window.location.search);
    const isMobileApp = urlParams.get("hideHeader") === "true";

    // Hide the header if either condition is met
    setHideHeader(inIframe || isMobileApp);
  }, []);

  const alertBeforeMinutes = parseInt(
    import.meta.env.DISHA_EXPIRY_ALERT_BEFORE_MINUTES || "5",
    10,
  );

  useEffect(() => {
    // Grab total minutes from env
    const totalMinutes = parseInt(
      import.meta.env.DISHA_TOKEN_EXPIRY_MINUTES || "60",
      10,
    );

    // Subtract alertBeforeMinutes for the warning, and convert to milliseconds
    const warningMinutes = totalMinutes - alertBeforeMinutes;
    const WARNING_TIME_MS = warningMinutes * 60 * 1000;

    const timer = setTimeout(() => {
      setShowExpiryWarning(true);
    }, WARNING_TIME_MS);

    return () => clearTimeout(timer);
  }, [alertBeforeMinutes]);

  return (
    <div className="bg-linear-to-br from-stone-50 h-screen overflow-hidden relative to-teal-50/40 via-emerald-50/30">
      {/* Organic background pattern */}
      <div
        className="fixed inset-0 opacity-40 pointer-events-none"
        style={{
          backgroundImage: `
            radial-gradient(ellipse at 20% 30%, rgba(16, 185, 129, 0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 70%, rgba(20, 184, 166, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(245, 245, 244, 0.5) 0%, transparent 70%)
          `,
        }}
      />

      {/* Subtle dot pattern */}
      <div
        className="fixed inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(16, 185, 129, 0.15) 1px, transparent 0)`,
          backgroundSize: "32px 32px",
        }}
      />

      {/* Main container with max-width for desktop */}
      <div className="backdrop-blur-sm bg-white/30 flex flex-col h-full max-w-2xl mx-auto relative shadow-2xl shadow-stone-900/5">
        {/* Header */}
        {!hideHeader && (
          <motion.header
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="backdrop-blur-md bg-white/70 border-b border-stone-200/50 flex items-center justify-between px-5 py-4 sticky top-0 z-10"
          >
            <div className="flex gap-3 items-center">
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                  rotate: [0, 2, -2, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "easeInOut",
                }}
                className="text-3xl"
              >
                🌿
              </motion.div>
              <div>
                <h1 className="font-bold text-stone-800 text-xl tracking-tight">
                  Disha
                </h1>
                <p className="font-medium text-stone-500 text-xs">
                  Your mental health companion
                </p>
              </div>
            </div>

            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{
                duration: 2,
                repeat: Number.POSITIVE_INFINITY,
                ease: "easeInOut",
              }}
              className="bg-emerald-50 border border-emerald-200/50 flex gap-2 items-center px-3 py-1.5 rounded-full"
            >
              <div className="bg-emerald-500 h-2 rounded-full w-2" />
              <span className="font-medium text-emerald-700 text-xs">
                Online
              </span>
            </motion.div>
          </motion.header>
        )}

        {/* Expiration Warning Banner */}
        {showExpiryWarning && !error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-indigo-50 border border-indigo-200 flex items-start justify-between mt-4 mx-4 p-4 rounded-xl shadow-sm shrink-0 z-10 relative"
          >
            <div className="flex gap-3 items-start">
              <span className="text-xl leading-none">⏳</span>
              <p className="text-indigo-800 text-sm leading-relaxed">
                <b>Just a gentle heads-up:</b> to protect your privacy, this
                secure session will expire in about {alertBeforeMinutes}{" "}
                minutes. If the page refreshes, your chat will clear to give you
                a fresh slate. Please take your time wrapping up your thoughts.
              </p>
            </div>
            <button
              onClick={() => setShowExpiryWarning(false)}
              type="button"
              className="hover:text-indigo-900 text-indigo-400 transition-colors ml-4 shrink-0 mt-0.5"
              aria-label="Dismiss warning"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </motion.div>
        )}

        {/* Weekly Cap Warning Banner */}
        {weeklyWarningMessage && !error && !isBlocked && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-orange-50 border border-orange-200 flex items-start justify-between mt-4 mx-4 p-4 rounded-xl shadow-sm shrink-0 z-10 relative"
          >
            <div className="flex gap-3 items-start">
              <span className="text-xl leading-none">📊</span>
              <p className="text-orange-800 text-sm leading-relaxed whitespace-pre-wrap">
                <b>{weeklyWarningMessage}</b>
              </p>
            </div>
            <button
              onClick={() => setWeeklyWarningMessage(null)}
              type="button"
              className="hover:text-orange-900 text-orange-400 transition-colors ml-4 shrink-0 mt-0.5"
              aria-label="Dismiss warning"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </motion.div>
        )}

        {/* Error banner */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-rose-50 border border-rose-200 flex items-center justify-between mt-4 mx-4 p-4 rounded-xl"
          >
            <p className="text-rose-700 text-sm">{error}</p>
            <button
              onClick={clearError}
              type="button"
              className="hover:text-rose-700 text-rose-500 transition-colors"
              aria-label="Dismiss error"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </motion.div>
        )}

        {/* Message list */}
        <MessageList
          messages={messages}
          onSuggestionSelect={sendMessage}
          isLoading={isLoading}
          messagesEndRef={messagesEndRef}
          showCheckpoint={showCheckpoint}
          isCheckpointSubmitting={isCheckpointSubmitting}
          onCheckpointSubmit={handleCheckpointSubmit}
          isBlocked={isBlocked}
        />

        {/* Chat input */}
        <ChatInput
          onSend={sendMessage}
          disabled={isLoading || isBlocked}
          isBlocked={isBlocked}
        />

        {/* Privacy footer */}
        <div className="bg-white/40 border-stone-100 border-t px-4 py-2 text-center">
          <p className="text-[11px] text-stone-400">
            🔒 Your conversations are private and secure
          </p>
        </div>
      </div>
    </div>
  );
}

export default App;
